import { useState, useRef, useCallback } from 'react'

import { ConditionField, FilterCondition } from 'common/filter/interfaces'
import {
  FieldTypeDefaultOperators,
  UNARY_OPERATORS,
} from 'common/filter/models/AudienceFilter/constants'
import { createCondition } from 'common/filter/models/AudienceFilter/helpers'

interface UseConditionPopoversOptions {
  condition: FilterCondition
  setCondition: React.Dispatch<React.SetStateAction<FilterCondition>>
  saveCondition: (conditionToSave: FilterCondition) => void
  isBuilder: boolean
}

export const useConditionPopovers = (options: UseConditionPopoversOptions) => {
  const { condition, setCondition, saveCondition, isBuilder } = options

  const [valuePopoverAnchor, setValuePopoverAnchor] = useState<HTMLElement | null>(null)
  const [fieldPopoverAnchor, setFieldPopoverAnchor] = useState<HTMLElement | null>(null)

  const valueRef = useRef<HTMLElement | null>(null)

  const handleValueRef = useCallback(
    (element: Nullable<HTMLElement>) => {
      const { operator, value } = condition

      if (
        operator &&
        !valueRef.current &&
        (value == null || (value && Array.isArray(value) && value.length === 0)) &&
        !UNARY_OPERATORS.includes(operator)
      ) {
        setValuePopoverAnchor(element)
        setFieldPopoverAnchor(null)
      }

      valueRef.current = element
    },
    [condition],
  )

  const closeValuePopover = useCallback(() => {
    setValuePopoverAnchor(null)
    saveCondition(condition)
  }, [saveCondition, condition])

  const closeFieldPopover = useCallback(() => {
    setFieldPopoverAnchor(null)
    saveCondition(condition)
  }, [saveCondition, condition])

  const handleFieldLinkClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setFieldPopoverAnchor(event.currentTarget)
    },
    [setFieldPopoverAnchor],
  )

  const handleValueLinkClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setValuePopoverAnchor(event.currentTarget)
    setFieldPopoverAnchor(null)
  }, [])

  const getCallbackOnFieldSelect = useCallback(
    (field: ConditionField) => {
      if (FieldTypeDefaultOperators[field.type]) {
        return closeFieldPopover
      }

      return () => {
        setValuePopoverAnchor(valueRef.current)
        setFieldPopoverAnchor(null)
      }
    },
    [closeFieldPopover],
  )

  const handleFieldSelect = useCallback(
    (field: ConditionField) => {
      const newCondition = createCondition(field, null, null, { isBuilder })
      newCondition._oid = condition._oid
      const callback = getCallbackOnFieldSelect(field)
      setCondition(newCondition)
      // Don't save immediately, let the callback handle popover state
      callback()
    },
    [condition._oid, isBuilder, getCallbackOnFieldSelect, setCondition],
  )

  return {
    valuePopoverAnchor,
    fieldPopoverAnchor,
    setValuePopoverAnchor,
    setFieldPopoverAnchor,
    onValueRefChange: handleValueRef,
    onValuePopoverClose: closeValuePopover,
    onFieldPopoverClose: closeFieldPopover,
    onFieldLinkClick: handleFieldLinkClick,
    onValueLinkClick: handleValueLinkClick,
    onFieldSelect: handleFieldSelect,
  }
}
